import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {finalize} from 'rxjs/operators';
import {isNullOrUndefined} from '../shared/is-null-or-undefined';
import {RestService} from '../services/rest.service';
import {getOrderSourceLabel} from '../services/order-source-labels';
import {GoogleMap, MapInfoWindow, MapMarker} from '@angular/google-maps';

@Component({
    selector: 'app-order-details',
    templateUrl: './order-details.component.html',
    styleUrls: ['./order-details.component.css']
})
export class OrderDetailsComponent implements OnChanges, OnDestroy {
    @Input() order: any;
    @Output() closed = new EventEmitter<void>();
    @ViewChild('orderRouteMap', {static: false}) orderRouteMap: GoogleMap;
    @ViewChild('routeMarkerInfo', {read: MapInfoWindow, static: false}) routeMarkerInfo: MapInfoWindow;
    @ViewChild('routePointInfo', {read: MapInfoWindow, static: false}) routePointInfo: MapInfoWindow;

    routePoints: Array<any> = [];
    mapMarkers: Array<any> = [];
    taximeterPoints: Array<any> = [];
    interactiveTaximeterPoints: Array<any> = [];
    taximeterPointIcon = {
        path: 0,
        fillColor: '#ef4444',
        fillOpacity: 1,
        scale: 4,
        strokeColor: '#ffffff',
        strokeOpacity: 0.95,
        strokeWeight: 1
    };
    fallbackDestinationIcon = {
        path: 0,
        fillColor: '#dc2626',
        fillOpacity: 1,
        scale: 22,
        strokeColor: '#0f172a',
        strokeOpacity: 1,
        strokeWeight: 4
    };
    fallbackDestinationLabel = {
        color: '#ffffff',
        fontSize: '19px',
        fontWeight: '900',
        text: 'D'
    };
    mapLat = 44.429021;
    mapLng = 26.103849;
    mapCenter: google.maps.LatLngLiteral = {lat: this.mapLat, lng: this.mapLng};
    mapZoom = 13;
    addressPolylineOptions: google.maps.PolylineOptions = {
        strokeColor: '#efc34c',
        strokeOpacity: 0.9,
        strokeWeight: 4
    };
    taximeterPolylineOptions: google.maps.PolylineOptions = {
        strokeColor: '#ef4444',
        strokeOpacity: 0.95,
        strokeWeight: 4
    };
    routePointInfoOptions: google.maps.InfoWindowOptions = {disableAutoPan: true};
    selectedMapMarker: any;
    selectedRoutePoint: any;
    isRouteLoading = false;
    routeLoadError = '';
    routeLookupComplete = false;
    isRouteTooltipOpen = false;
    private routeRequestOrderId = '';
    private shouldFitOrderMap = false;

    constructor(private restService: RestService) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!changes.order) {
            return;
        }
        if (isNullOrUndefined(this.order)) {
            this.resetRoute();
            this.unlockBodyScroll();
            return;
        }

        this.setRouteData(this.order);
        this.loadOrderLocations(this.order);
        document.body.style.overflow = 'hidden';
    }

    ngOnDestroy() {
        this.unlockBodyScroll();
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapeKey(event: KeyboardEvent) {
        if (isNullOrUndefined(this.order)) {
            return;
        }
        event.preventDefault();
        this.close();
    }

    close() {
        this.resetRoute();
        this.unlockBodyScroll();
        this.closed.emit();
    }

    getOrder(orderWrapper: any) {
        return isNullOrUndefined(orderWrapper) ? undefined : orderWrapper.Order || orderWrapper;
    }

    getRequest(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        return isNullOrUndefined(order) ? undefined : order.InitialOrderRequest;
    }

    getCustomer(orderWrapper: any) {
        const request = this.getRequest(orderWrapper);
        return isNullOrUndefined(request) ? undefined : request.Customer;
    }

    getAssignedCar(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        return isNullOrUndefined(order) ? undefined : order.AssignedCar;
    }

    getDriver(orderWrapper: any) {
        const car = this.getAssignedCar(orderWrapper);
        return isNullOrUndefined(car) ? undefined : car.Driver;
    }

    getOrderIdentifier(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        return this.valueOrDash(isNullOrUndefined(order) ? undefined : order.OrderIdentifier);
    }

    getOrderStatus(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        if (!isNullOrUndefined(orderWrapper) && this.hasValue(orderWrapper.CloseReason)) {
            return orderWrapper.CloseReason;
        }
        return this.valueOrDash(isNullOrUndefined(order) ? undefined : order.OrderState);
    }

    getClientName(orderWrapper: any) {
        const customer = this.getCustomer(orderWrapper);
        return this.valueOrDash(isNullOrUndefined(customer) ? undefined : customer.Name);
    }

    getClientPhone(orderWrapper: any) {
        const customer = this.getCustomer(orderWrapper);
        return this.valueOrDash(isNullOrUndefined(customer) ? undefined : customer.PhoneNumber);
    }

    getClientEmail(orderWrapper: any) {
        const customer = this.getCustomer(orderWrapper);
        return this.valueOrDash(isNullOrUndefined(customer) ? undefined : customer.Email);
    }

    getPickupAddress(orderWrapper: any) {
        const request = this.getRequest(orderWrapper);
        return isNullOrUndefined(request) ? undefined : request.PickupAddress;
    }

    getDestinationAddress(orderWrapper: any) {
        const request = this.getRequest(orderWrapper);
        return isNullOrUndefined(request) ? undefined : request.DestinationAddress;
    }

    getStops(orderWrapper: any) {
        const request = this.getRequest(orderWrapper);
        return isNullOrUndefined(request) || !Array.isArray(request.Stops) ? [] : request.Stops;
    }

    getEntryDate(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        return isNullOrUndefined(order) ? undefined : order.EntryDate;
    }

    getCloseDate(orderWrapper: any) {
        if (!isNullOrUndefined(orderWrapper) && !isNullOrUndefined(orderWrapper.CloseDate_iso)) {
            return orderWrapper.CloseDate_iso;
        }
        return isNullOrUndefined(orderWrapper) ? undefined : orderWrapper.CloseDate;
    }

    getRequestValue(orderWrapper: any, key: string) {
        const request = this.getRequest(orderWrapper);
        return this.valueOrDash(isNullOrUndefined(request) ? undefined : request[key]);
    }

    getOrderSource(orderWrapper: any) {
        return getOrderSourceLabel(this.getRequestValue(orderWrapper, 'DevicePlatform'));
    }

    getOrderDate(orderWrapper: any, key: string) {
        const order = this.getOrder(orderWrapper);
        if (isNullOrUndefined(order) || isNullOrUndefined(order[key])) {
            return undefined;
        }

        const value = new Date(order[key]);
        return isNaN(value.getTime()) || value.getUTCFullYear() <= 1 ? undefined : order[key];
    }

    getCarValue(orderWrapper: any, key: string) {
        const car = this.getAssignedCar(orderWrapper);
        return this.valueOrDash(isNullOrUndefined(car) ? undefined : car[key]);
    }

    getDriverValue(orderWrapper: any, key: string) {
        const driver = this.getDriver(orderWrapper);
        return this.valueOrDash(isNullOrUndefined(driver) ? undefined : driver[key]);
    }

    getServiceValue(orderWrapper: any, key: string) {
        const request = this.getRequest(orderWrapper);
        const service = isNullOrUndefined(request) ? undefined : request.Service;
        return this.valueOrDash(isNullOrUndefined(service) ? undefined : service[key]);
    }

    getCarDisplay(orderWrapper: any) {
        const car = this.getAssignedCar(orderWrapper);
        if (isNullOrUndefined(car)) {
            return '-';
        }
        const companyAndId = String(car.CompanyIdentifier || '') + String(car.CarId || '');
        return companyAndId !== '' ? companyAndId : this.valueOrDash(car.CarIdentifier);
    }

    getPrice(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        const value = Number(isNullOrUndefined(order) ? undefined : order.TaximeterTotalPrice);
        return isNaN(value) ? '-' : value.toFixed(2) + ' lei';
    }

    getDistance(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        const value = Number(isNullOrUndefined(order) ? undefined : order.BillableDistance);
        return isNaN(value) ? '-' : (value / 1000).toFixed(2) + ' km';
    }

    getDuration(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        const seconds = Number(isNullOrUndefined(order) ? undefined : order.BillableSeconds);
        if (isNaN(seconds)) {
            return '-';
        }
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return (hours > 0 ? hours + 'h ' : '') + minutes + 'm ' + remainingSeconds + 's';
    }

    formatAddress(address: any) {
        if (isNullOrUndefined(address)) {
            return '-';
        }
        if (this.hasValue(address.CompleteAddress)) {
            return address.CompleteAddress;
        }
        const parts = [];
        if (this.hasValue(address.StreetName)) {
            parts.push(address.StreetName);
        }
        if (this.hasValue(address.StreetNumber)) {
            parts.push('Nr. ' + address.StreetNumber);
        }
        if (this.hasValue(address.BlocNumber)) {
            parts.push('Bl. ' + address.BlocNumber);
        }
        if (this.hasValue(address.ScaraNumber)) {
            parts.push('Sc. ' + address.ScaraNumber);
        }
        if (parts.length === 0 && this.hasValue(address.Locality)) {
            parts.push(address.Locality);
        }
        return parts.length === 0 ? '-' : parts.join(', ');
    }

    formatBoolean(value: any) {
        return isNullOrUndefined(value) ? '-' : value ? 'Yes' : 'No';
    }

    hasMapData() {
        return this.mapMarkers.length > 0 || this.taximeterPoints.length > 0;
    }

    hasMultipleMapPoints() {
        return this.mapMarkers.length + this.taximeterPoints.length > 1;
    }

    formatRouteSpeed(speed: any) {
        const value = Number(speed);
        return isNaN(value) ? '-' : (value * 3.6).toFixed(1) + ' km/h';
    }

    openOrderMarkerInfo(mapMarker: MapMarker, marker: any) {
        this.selectedMapMarker = marker;
        this.routeMarkerInfo.open(mapMarker);
    }

    showRoutePointInfo(mapMarker: MapMarker, point: any) {
        this.isRouteTooltipOpen = true;
        this.selectedRoutePoint = point;
        this.routePointInfo.open(mapMarker);
    }

    hideRoutePointInfo() {
        this.isRouteTooltipOpen = false;
        this.selectedRoutePoint = undefined;
        this.routePointInfo.close();
    }

    fitOrderMapBounds() {
        const points = this.getAllMapPoints();
        if (!this.shouldFitOrderMap || !this.orderRouteMap || points.length < 2) {
            return;
        }

        this.shouldFitOrderMap = false;
        this.orderRouteMap.fitBounds(this.getMapBounds(points), 48);
    }

    private setRouteData(orderWrapper: any) {
        const points = [{label: 'Pickup', marker: 'P', type: 'pickup', address: this.getPickupAddress(orderWrapper)}];
        this.getStops(orderWrapper).forEach((stop, index) => {
            points.push({label: 'Stop ' + (index + 1), marker: String(index + 1), type: 'stop', address: stop});
        });
        points.push({
            label: 'Destination',
            marker: 'D',
            type: 'destination',
            address: this.getDestinationAddress(orderWrapper)
        });
        this.routePoints = points
            .filter(point => !isNullOrUndefined(point.address))
            .map(point => ({
                label: point.label,
                marker: point.marker,
                type: point.type,
                address: point.address,
                formattedAddress: this.formatAddress(point.address),
                coords: this.getAddressCoords(point.address)
            }));
        this.mapMarkers = this.routePoints
            .filter(point => !isNullOrUndefined(point.coords))
            .map(point => ({
                lat: point.coords.lat,
                lng: point.coords.lng,
                position: {lat: point.coords.lat, lng: point.coords.lng},
                title: point.label + ': ' + point.formattedAddress,
                type: point.type,
                options: {
                    icon: this.getMarkerIcon(point.type),
                    zIndex: 20
                },
                labelOptions: {
                    color: point.type === 'stop' ? '#111827' : '#ffffff',
                    fontSize: '19px',
                    fontWeight: '900',
                    text: point.marker
                }
            }));
        this.updateMapViewport();
    }

    private loadOrderLocations(orderWrapper: any) {
        const orderId = this.getRawOrderIdentifier(orderWrapper);
        this.taximeterPoints = [];
        this.interactiveTaximeterPoints = [];
        this.routeLoadError = '';
        this.routeLookupComplete = false;
        this.routeRequestOrderId = orderId;

        if (orderId === '') {
            this.isRouteLoading = false;
            this.routeLookupComplete = true;
            return;
        }

        this.isRouteLoading = true;
        this.restService.getOrderLocations(orderId)
            .pipe(finalize(() => {
                if (this.routeRequestOrderId === orderId) {
                    this.isRouteLoading = false;
                    this.routeLookupComplete = true;
                }
            }))
            .subscribe(
                (response: any) => this.applyOrderLocationsResponse(response, orderId),
                () => {
                    if (this.routeRequestOrderId !== orderId) {
                        return;
                    }
                    this.taximeterPoints = [];
                    this.interactiveTaximeterPoints = [];
                    this.routeLoadError = 'The recorded route could not be loaded.';
                    this.updateMapViewport();
                }
            );
    }

    private applyOrderLocationsResponse(response: any, orderId: string) {
        if (this.routeRequestOrderId !== orderId || this.getRawOrderIdentifier(this.order) !== orderId) {
            return;
        }

        const parsedResponse = this.parseResponse(response);
        const taximeterOrder = isNullOrUndefined(parsedResponse) ? undefined : parsedResponse.TaximeterOrder;
        const locations = !isNullOrUndefined(taximeterOrder) && Array.isArray(taximeterOrder.Locations)
            ? taximeterOrder.Locations : [];

        this.taximeterPoints = locations
            .map((location, index) => {
                const lat = Number(location.Latitude);
                const lng = Number(location.Longitude);
                const speed = Number(location.Speed);
                const localTimestamp = this.toLocalRouteTimestamp(location.Timestamp);
                return {
                    lat,
                    lng,
                    speed: isNaN(speed) ? undefined : speed,
                    localTimestamp,
                    timestampValue: isNullOrUndefined(localTimestamp) ? undefined : localTimestamp.getTime(),
                    originalIndex: index,
                    isFallbackDestination: false
                };
            })
            .filter(point => !isNaN(point.lat) && !isNaN(point.lng) && point.lat !== 0 && point.lng !== 0)
            .sort((first, second) => {
                if (!isNullOrUndefined(first.timestampValue) && !isNullOrUndefined(second.timestampValue)) {
                    return first.timestampValue - second.timestampValue;
                }
                return first.originalIndex - second.originalIndex;
            });

        const hasDestinationMarker = this.mapMarkers.some(marker => marker.type === 'destination');
        if (!hasDestinationMarker && this.taximeterPoints.length > 0) {
            this.taximeterPoints[this.taximeterPoints.length - 1].isFallbackDestination = true;
        }
        this.interactiveTaximeterPoints = this.sampleRoutePoints(this.taximeterPoints, 100)
            .map(point => Object.assign({}, point, {
                position: {lat: point.lat, lng: point.lng},
                labelOptions: point.isFallbackDestination ? this.fallbackDestinationLabel : undefined,
                options: {
                    icon: point.isFallbackDestination ? this.fallbackDestinationIcon : this.taximeterPointIcon,
                    zIndex: 2
                }
            }));
        this.updateMapViewport();
    }

    private parseResponse(response: any) {
        if (typeof response !== 'string') {
            return response;
        }
        try {
            return JSON.parse(response);
        } catch (error) {
            return {};
        }
    }

    private toLocalRouteTimestamp(timestamp: any) {
        if (isNullOrUndefined(timestamp) || timestamp === '') {
            return undefined;
        }
        const timestampText = String(timestamp);
        const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(timestampText);
        const utcTimestamp = hasTimezone ? timestampText : timestampText + 'Z';
        const localDate = new Date(utcTimestamp);
        return isNaN(localDate.getTime()) ? undefined : localDate;
    }

    private sampleRoutePoints(points: Array<any>, maximumPoints: number) {
        if (points.length <= maximumPoints) {
            return points;
        }
        const sampledPoints = [];
        const lastIndex = points.length - 1;
        for (let index = 0; index < maximumPoints; index++) {
            const pointIndex = Math.round(index * lastIndex / (maximumPoints - 1));
            sampledPoints.push(points[pointIndex]);
        }
        return sampledPoints;
    }

    private getRawOrderIdentifier(orderWrapper: any) {
        const order = this.getOrder(orderWrapper);
        return isNullOrUndefined(order) || !this.hasValue(order.OrderIdentifier)
            ? '' : String(order.OrderIdentifier);
    }

    private updateMapViewport() {
        const points = this.taximeterPoints.length > 0
            ? this.taximeterPoints.concat(this.mapMarkers)
            : this.mapMarkers;
        if (points.length === 0) {
            return;
        }
        const bounds = points.reduce((result, point) => ({
            minLat: Math.min(result.minLat, point.lat),
            maxLat: Math.max(result.maxLat, point.lat),
            minLng: Math.min(result.minLng, point.lng),
            maxLng: Math.max(result.maxLng, point.lng)
        }), {
            minLat: points[0].lat,
            maxLat: points[0].lat,
            minLng: points[0].lng,
            maxLng: points[0].lng
        });
        this.mapLat = (bounds.minLat + bounds.maxLat) / 2;
        this.mapLng = (bounds.minLng + bounds.maxLng) / 2;
        this.mapCenter = {lat: this.mapLat, lng: this.mapLng};
        this.mapZoom = points.length > 1 ? 12 : 15;
        this.shouldFitOrderMap = points.length > 1;
    }

    private getAllMapPoints(): Array<any> {
        return this.taximeterPoints.length > 0
            ? this.taximeterPoints.concat(this.mapMarkers)
            : this.mapMarkers;
    }

    private getMapBounds(points: Array<any>): google.maps.LatLngBoundsLiteral {
        return points.reduce((bounds, point) => ({
            north: Math.max(bounds.north, point.lat),
            south: Math.min(bounds.south, point.lat),
            east: Math.max(bounds.east, point.lng),
            west: Math.min(bounds.west, point.lng)
        }), {
            north: points[0].lat,
            south: points[0].lat,
            east: points[0].lng,
            west: points[0].lng
        });
    }

    private getAddressCoords(address: any) {
        if (isNullOrUndefined(address)) {
            return undefined;
        }
        const coords = address.Coords || address;
        const lat = Number(coords.GpsLat || coords.Latitude || coords.latitude || coords.Latitudine || coords.lat);
        const lng = Number(coords.GpsLong || coords.Longitude || coords.longitude || coords.Longitudine || coords.lng);
        return isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0 ? undefined : {lat, lng};
    }

    private getMarkerIcon(type: string) {
        const fillColor = type === 'pickup' ? '#16a34a'
            : type === 'destination' ? '#dc2626' : '#facc15';
        return {
            path: 0,
            fillColor,
            fillOpacity: 1,
            scale: 22,
            strokeColor: '#0f172a',
            strokeOpacity: 1,
            strokeWeight: 4
        };
    }

    private hasValue(value: any) {
        return !isNullOrUndefined(value) && String(value).trim() !== '';
    }

    private valueOrDash(value: any) {
        return this.hasValue(value) ? value : '-';
    }

    private resetRoute() {
        this.routePoints = [];
        this.mapMarkers = [];
        this.taximeterPoints = [];
        this.interactiveTaximeterPoints = [];
        this.isRouteLoading = false;
        this.routeLoadError = '';
        this.routeLookupComplete = false;
        this.isRouteTooltipOpen = false;
        this.selectedMapMarker = undefined;
        this.selectedRoutePoint = undefined;
        this.shouldFitOrderMap = false;
        this.routeRequestOrderId = '';
        this.mapLat = 44.429021;
        this.mapLng = 26.103849;
        this.mapCenter = {lat: this.mapLat, lng: this.mapLng};
        this.mapZoom = 13;
    }

    private unlockBodyScroll() {
        document.body.style.overflow = '';
    }
}
